import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../../atoms/Button';
import { Icon } from '../../atoms/Icon';
import { Link } from '../../atoms/Link';
import './AccountButton.css';

export interface DropdownLink {
  label: string;
  href: string;
  icon?: 'logout';
}

export interface AccountButtonProps {
  isLoggedIn: boolean;
  dropdownLinks?: DropdownLink[];
  loginHref?: string;
  className?: string;
  style?: React.CSSProperties;
}

export const AccountButton: React.FC<AccountButtonProps> = ({
  isLoggedIn,
  dropdownLinks = [],
  loginHref = '/manage',
  className = '',
  style,
}) => {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking/tabbing outside (only active when logged in)
  useEffect(() => {
    if (!isLoggedIn) return;
    const handleClickOutside = (event: Event) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsKeyboardOpen(false);
      }
    };

    const handleFocusOut = (event: Event) => {
      const focusEvent = event as FocusEvent;
      if (
        containerRef.current &&
        !containerRef.current.contains(focusEvent.relatedTarget as Node)
      ) {
        setIsKeyboardOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('focusout', handleFocusOut);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [isLoggedIn]);

  // Toggle dropdown (responds to both click and keyboard Enter/Space)
  const handleToggleDropdown = () => {
    setIsKeyboardOpen(!isKeyboardOpen);
  };

  // Show login button when not logged in
  if (!isLoggedIn) {
    return (
      <div className={`hi-ui hi-ui-account-button ${className}`.trim()} style={style}>
        <Button variant="success" size="medium" href={loginHref}>
          LOG IN
          <Icon name="user" size={13} />
        </Button>
      </div>
    );
  }

  // Show account dropdown when logged in
  return (
    <div
      ref={containerRef}
      className={`hi-ui hi-ui-account-button ${className}`.trim()}
      style={{
        position: 'relative',
        ...style,
      }}
    >
      {/* Account button */}
      <Button
        variant="success"
        size="medium"
        className="hi-ui-account-button-trigger"
        onClick={handleToggleDropdown}
        aria-expanded={isKeyboardOpen}
        aria-haspopup="true"
      >
        ACCOUNT
        <Icon name="user" size={13} />
      </Button>

      {/* Dropdown menu - shows on hover or keyboard toggle */}
      <div
        className={`hi-ui-account-button-menu ${isKeyboardOpen ? 'hi-ui-account-button-menu-keyboard-open' : ''}`}
        role="menu"
        aria-hidden={!isKeyboardOpen}
      >
        {dropdownLinks.map((link, index) => (
          <Link
            key={index}
            href={link.href}
            className="hi-ui-account-button-link"
            noUnderline
            role="menuitem"
            tabIndex={isKeyboardOpen ? 0 : -1}
          >
            {link.label}
            {link.icon === 'logout' && (
              <Icon name="logout" size={16} style={{ marginLeft: '8px' }} />
            )}
          </Link>
        ))}
      </div>
    </div>
  );
};
